import {
  FieldTypes,
  IFieldInstance,
} from "../../types/fieldInstance.interface";
import {
  getRLFormFields,
  getRLType,
  getSelectedValueLabel,
} from "./relatedListHelpers";
import moment from "moment";
import { getSignatoryRoleLabel } from "../formGenUtils/approvalUtils";
import {
  reportBodyTemplate,
  formatForRLsInReport,
} from "./incidentReportConstants";
import {
  shouldFieldRender,
  checkIncidentType,
} from "../formGenUtils/viewModelMethods";
import { findFormfieldById } from "../formGenUtils/hashTable";
import { ESTIMATED_DATE_TIME_LABEL } from "../../components/EstimatedDateTime";

type HeaderObject = {
  facilityName: string;
  mrn: string;
  residentName: string;
  dob: string;
  location: string;
  incidentType: string;
  incidentNumber: string;
  /** Date and time the nurse entered for the incident*/
  facilityDateTime: string;
  incidentStatus: string;
  /** true when Unknown_Date_and_Time__c is set on the incident. */
  isEstimatedDateTime: boolean;
};

/**
 * Returns a header object for reports module
 * @param {Object<string, any>} incidentObj - Object with string keys and any type values
 * @param incidentNumber - String returned with Signatory information
 * @returns {HeaderObject} - Header object
 */
export function generateHeaderObject(
  incidentObj: { [s: string]: any },
  incidentNumber: string
): HeaderObject {
  // Function logic goes here
  const headerObject = {
    facilityName: incidentObj.Facility__r?.Name || "",
    mrn: incidentObj.Resident__r?.Medical_Record_Number__c || "",
    residentName: incidentObj.Resident__r?.Resident_Full_Name__c || "",
    dob: incidentObj?.Resident__r?.Birth_Date__c || "",
    location: `${incidentObj?.Resident__r?.Room_Description__c || "-"}/${
      incidentObj?.Resident__r?.Unit_Description__c || "-"
    }/${incidentObj?.Resident__r?.Bed_Description__c || "-"}`,
    incidentType: incidentObj?.RecordType?.Name || "",
    incidentNumber: incidentNumber || "",
    facilityDateTime: incidentObj?.Incident_Date_Time__c || "",
    incidentStatus: incidentObj?.Incident_Status__c || "",
    isEstimatedDateTime: Boolean(incidentObj?.Unknown_Date_and_Time__c),
  };

  return headerObject;
}

// Take the report template, the incident object, the tab separated form datasheet,
// map through the reportBodyTemplate, for each object, find all fields and associated values, return an array of arrays.
// If the length of the array is one and the element array has no subheading value, render without subheading component.
// If the length is more than one, render the individual elements in their own subsections, with their subheadings.
type ReportFields = {
  fieldNames: string[] | string;
  sourceTab: string;
  sourceSection?: string;
  subSection?: string;
  excludedFields?: string[];
};

export function getRelevantFields(
  tabSeparatedFormFields: { [x: string]: any },
  fieldSpec: ReportFields,
  incidentTypes: any[],
  incidentType: string
) {
  const { sourceTab, sourceSection, fieldNames, excludedFields } = fieldSpec;
  let sectionFields = tabSeparatedFormFields[sourceTab];
  // If sourceSection is defined, narrow down the section fields
  if (sourceSection) {
    sectionFields = sectionFields.filter(
      (field: IFieldInstance) => field.section === sourceSection
    );
  }

  // Set selected fields based on fieldNames property
  let selectedFields = sectionFields; // All fields;
  if (fieldNames !== "*" && fieldNames.length) {
    selectedFields = sectionFields.filter((field: IFieldInstance) =>
      fieldNames.includes(field.fieldName)
    ); // Specific fields
  }

  // filter for fields valid for incident
  if (incidentType) {
    const incidentLabel = incidentTypes.find(
      (_incidentType: { label: string }) => _incidentType.label === incidentType
    )?.id;
    selectedFields = selectedFields.filter((field: IFieldInstance) =>
      field.incidentType.includes(incidentLabel)
    );
  }

  // Exclude fields if specified
  if (excludedFields) {
    selectedFields = selectedFields.filter(
      (field: IFieldInstance) => !excludedFields.includes(field.fieldName)
    );
  }
  return selectedFields;
}

function backupTextField(
  field: IFieldInstance,
  fields: IFieldInstance[],
  incidentObj: any
) {
  // Check if field is lookup or picklist and has a toggle field attached,
  // check if toggle field is true and return textField Name
  let textFieldName = "";
  let toggleBoxName = "";
  if (
    field.fieldType === FieldTypes.Lookup ||
    field.fieldType === FieldTypes.Picklist
  ) {
    const toggleBox = fields.find(
      (_field) =>
        field.childFieldIds.includes(_field.id) &&
        _field.fieldType === FieldTypes.Togglebox
    );
    if (toggleBox) {
      const textField = fields.find(
        (_field) =>
          toggleBox.childFieldIds.includes(_field.id) &&
          _field.fieldType === FieldTypes.Text
      );
      toggleBoxName = toggleBox?.fieldName ?? "";
      textFieldName = textField?.fieldName ?? "";
    }
  }

  return toggleBoxName && incidentObj[toggleBoxName]
    ? incidentObj[textFieldName]
    : "";
}

export function processStringFormat(
  input: string,
  data: Record<string, any>
): string {
  const regex = /\$<([^>]+)>/g;
  return input.replace(regex, (_, match) => {
    const variables = match
      .split(",")
      .map((variable: string) => variable.trim());
    let value = "";
    for (const variable of variables) {
      if (data[variable]) {
        value = data[variable];
        break;
      }
    }
    return value;
  });
}

export function formatDateString(date: any): string | null {
  const dateString = (date || "").includes("+")
    ? date.split("+")[0] + "Z"
    : date;
  const dateObj = new Date(dateString);
  return date ? moment(dateObj).format("MMMM Do YYYY, h:mm a") : null;
}

export function transformFieldValue(
  value: any,
  fields: { [key: string]: IFieldInstance[] },
  allLookUpOptions: any
) {
  if (!fields) return value;
  const transformedValues = { ...value };
  Object.keys(transformedValues).forEach((fieldName) => {
    const fieldDetails = fields[fieldName]?.[0];
    const fieldValue = transformedValues[fieldName];
    if (fieldDetails) {
      if (fieldDetails.fieldType === FieldTypes.Lookup) {
        transformedValues[fieldName] = getSelectedValueLabel(
          fieldValue,
          allLookUpOptions
        );
      }
      if (fieldDetails.fieldType === FieldTypes.DateTime) {
        transformedValues[fieldName] = formatDateString(fieldValue);
      }
    }
  });
  return transformedValues;
}

type RelatedListTemplate = {
  renderAsString?: boolean;
  renderFormat: string | string[];
  fieldNames: { fieldLabel: string; fieldValue: string[] | string }[];
};

export function formatRelatedListForReports(
  fieldName: string,
  fieldValue: any,
  allLookUpOptions: any,
  relatedListTemplate: {
    [x: string]: RelatedListTemplate;
  } = formatForRLsInReport
) {
  const rlType = getRLType(fieldName);
  const rlFields = getRLFormFields(fieldName);
  let _fieldValue = transformFieldValue(fieldValue, rlFields, allLookUpOptions);
  if (rlType) {
    const template = relatedListTemplate[rlType];
    // check fieldValue Object for fields in fieldName object
    // if no template is found, log error and return nothing
    if (!template) return;
    // get values for each field in fieldNames from fieldValue object
    if (template.renderAsString) {
      const renderFormat = template.renderFormat as string;
      return processStringFormat(renderFormat, _fieldValue);
    }
    const fieldValues = template.fieldNames.reduce(
      (acc: any, field: any, index: any) => {
        let cellValue;
        const fieldsForValue = field.fieldValue;
        const cellRenderFormat = Array.isArray(template.renderFormat)
          ? template.renderFormat[index]
          : template.renderFormat;
        if (typeof fieldsForValue === "string") {
          cellValue = _fieldValue?.[fieldsForValue];
        } else {
          cellValue = processStringFormat(cellRenderFormat, _fieldValue);
        }
        acc[field.fieldLabel] = cellValue || "";
        return acc;
      },
      {}
    );
    return fieldValues;
  }
}

export function shouldReportFieldRender(
  fieldValue: any,
  qapiHidden: boolean,
  isNonQapi: boolean,
  sectionLabel: string,
  includeStatements: boolean,
  includeConclusion = false
): boolean {
  // Statements/Conclusion are QAPI-only sections; their opt-in toggles un-hide them on the non-QAPI report.
  const _qapiHidden =
    (sectionLabel === "Statements" && includeStatements) ||
    (sectionLabel === "Conclusion" && includeConclusion)
      ? false
      : qapiHidden;
  return !!(fieldValue && (isNonQapi ? !_qapiHidden : true));
}

export function modifyFieldValue(
  field: IFieldInstance,
  fields: any[],
  fieldValue: any,
  allLookUpOptions: any[],
  selectedFields: IFieldInstance[],
  incidentObj: any
) {
  if (field.fieldType === FieldTypes.Lookup && fieldValue) {
    fieldValue = getSelectedValueLabel(fieldValue, allLookUpOptions);
  }
  const backupTextFieldValue = backupTextField(
    field,
    selectedFields,
    incidentObj
  );
  if (backupTextFieldValue) {
    fieldValue = backupTextFieldValue;
  }

  if (field.fieldType === FieldTypes.DateTime) {
    fieldValue = formatDateString(fieldValue);
  }

  const isParentBoolean = fields.find(
    (_field: { id: string; fieldType: string }) =>
      _field.id === field.parentFieldId &&
      _field.fieldType === FieldTypes.Togglebox
  );

  if (field.fieldType === FieldTypes.Text && isParentBoolean) {
    fieldValue = "";
  }

  if (field.fieldType === FieldTypes.Togglebox) {
    fieldValue = null;
  }

  if (field.fieldType === FieldTypes.Checkbox) {
    fieldValue = fieldValue ? "Yes" : "No";
  }

  if (
    (field.fieldType === FieldTypes.MultiSelectPicklist ||
      field.fieldType === FieldTypes.MultiSelectasCheckBoxes ||
      field.fieldType === FieldTypes.BranchedMultiSelectasCheckBoxes) &&
    Array.isArray(fieldValue) && !fieldValue.length
  ) {
    fieldValue = null;
  }

  if (field.fieldType === FieldTypes.RelatedList && Array.isArray(fieldValue)) {
    let _fieldValue = fieldValue.map((val: any) =>
      formatRelatedListForReports(field.fieldName, val, allLookUpOptions)
    );
    fieldValue = !fieldValue.length ? "No records reported" : _fieldValue;
  }
  return fieldValue;
}

function divideIntoSubsections(
  subSection: string | undefined,
  accumulatedData: { [x: string]: any },
  formattedSelectedFields: any[]
) {
  if (subSection) {
    accumulatedData[subSection] = accumulatedData[subSection]
      ? [...accumulatedData[subSection], ...formattedSelectedFields]
      : [...formattedSelectedFields];
  } else {
    accumulatedData["Default"] = accumulatedData["Default"]
      ? [...accumulatedData["Default"], ...formattedSelectedFields]
      : [...formattedSelectedFields];
  }
  return accumulatedData;
}

function appendSignatures(reportBody: any[], signatories: any[]) {
  const signatures: any = {};
  signatories.forEach((signatory) => {
    const eligibleSigners = getSignatoryRoleLabel(
      signatory?.Eligible_Signers__c
    );
    if (eligibleSigners) {
      signatures[eligibleSigners] = signatory?.Status__c
        ? [
            {
              fieldName: "approvedBy",
              fieldLabel: "Approved by",
              fieldValue: signatory?.Signed_By_Name_Text__c,
            },
            {
              fieldName: "dateApproved",
              fieldLabel: "Date Approved",
              fieldValue: signatory?.Signed_Date_Time__c
                ? moment(signatory?.Signed_Date_Time__c).format("MM/DD/YYYY")
                : null,
            },
            {
              fieldName: "approvalComment",
              fieldLabel: "Approval Comment",
              fieldValue: signatory?.Comment__c,
            },
          ]
        : [];
    }
  });
  const signaturesObj = { Signatures: signatures };
  if (signatories.length) reportBody.push(signaturesObj);
}

/**
 * Report-specific visibility gate. Extends shouldFieldRender to handle
 * inline child fields (parentFieldId present, no condition) which the form
 * renders inside their parent component. In the report these need to appear
 * as standalone fields when their parent is visible.
 */
function shouldFieldRenderForReport(
  field: IFieldInstance,
  values: any,
  findFieldById: (id: string) => IFieldInstance
): boolean {
  const parentField = field.parentFieldId
    ? findFieldById(field.parentFieldId)
    : null;

  if (!parentField) return true;

  if (field.condition) {
    // Has a condition — use the same full logic as the form view
    return shouldFieldRender(field, values, findFieldById);
  }

  // Has a parent but no condition — inline child component.
  // Show it when its parent is itself visible (recursive to handle nesting).
  return shouldFieldRenderForReport(parentField, values, findFieldById);
}

/**
 * Dynamically generates the QAPI report body by walking all fields in
 * tabSeparatedFormFields, applying the same visibility gates used by the
 * incident view form (incident-type check + conditional field rendering).
 */
function generateQapiBodyObject(
  incidentObj: { [s: string]: any },
  incidentTypes: any,
  allLookUpOptions: any[],
  tabSeparatedFormFields: { [s: string]: IFieldInstance[] },
  signatories: any[]
) {
  const incidentTypeName = incidentObj?.RecordType?.Name;
  const incidentTypeId = incidentTypes.find(
    (t: { label: string }) => t.label === incidentTypeName
  )?.id;

  const allFields = Object.values(tabSeparatedFormFields).flat() as IFieldInstance[];

  // Accumulate fields grouped by section name, preserving insertion order
  const sectionMap: Record<string, any[]> = {};

  allFields.forEach((field) => {
    // Gate 1: incident type
    if (!checkIncidentType(incidentTypeId, field.incidentType)) return;

    // Gate 2: conditional visibility — uses report-specific gate that also
    // includes inline child fields (no condition) by checking parent visibility
    if (!shouldFieldRenderForReport(field, incidentObj, findFormfieldById)) return;

    // Gate 3: transform value (same as existing report logic)
    let fieldValue = incidentObj[field.fieldName];
    fieldValue = modifyFieldValue(
      field,
      allFields,
      fieldValue,
      allLookUpOptions,
      allFields,
      incidentObj
    );

    // Gate 4: only show fields with a truthy value
    if (!fieldValue) return;

    // estimated incidents show the date row under the estimated label.
    const effectiveLabel =
      field.fieldName === "Incident_Date_Time__c" &&
      incidentObj?.Unknown_Date_and_Time__c
        ? ESTIMATED_DATE_TIME_LABEL
        : field.fieldLabel;

    const section = field.section || "General";
    if (!sectionMap[section]) sectionMap[section] = [];
    sectionMap[section].push({
      fieldName: field.fieldName,
      fieldLabel: effectiveLabel,
      fieldValue,
    });
  });

  // Convert to the array-of-objects shape QapiBody expects:
  // [{ "SectionName": { SubSectionKey: fields[] } }, ...]
  const reportBody = Object.entries(sectionMap).map(([sectionLabel, fields]) => {
    // Statements section: QapiBody expects { Statements: [...], Interviews: [...] }
    if (sectionLabel === "Statements") {
      const interviews = fields.filter((f) => f.fieldName === "Interviews__c");
      const rest = fields.filter((f) => f.fieldName !== "Interviews__c");
      return {
        [sectionLabel]: {
          Statements: rest,
          ...(interviews.length ? { Interviews: interviews } : {}),
        },
      };
    }
    return { [sectionLabel]: { Default: fields } };
  });

  appendSignatures(reportBody, signatories);
  return reportBody;
}

/**
 * Returns a body object for reports module
 * @param {Object<string, any>} incidentObj - Object with string keys and any type values
 * @param incidentTypes
 * @param allLookUpOptions
 * @param {Object<string, Array<IFieldInstance>>} tabSeparatedFormFields - Object with keys from a list of possible values and values as objects containing keys from incidentObj and their values
 * @param reportBodyTemplate
 * @param signatories
 * @param {Boolean} isNonQapi
 * @param {Boolean} includeStatements
 * @param {Boolean} includeConclusion
 */
export function generateBodyObject(
  incidentObj: { [s: string]: any },
  incidentTypes: any,
  allLookUpOptions: any[],
  tabSeparatedFormFields: { [s: string]: IFieldInstance[] },
  signatories: any[],
  isNonQapi: boolean,
  includeStatements: boolean,
  includeConclusion = false
) {
  // QAPI mode: dynamically walk all fields using form visibility logic
  if (!isNonQapi) {
    return generateQapiBodyObject(
      incidentObj,
      incidentTypes,
      allLookUpOptions,
      tabSeparatedFormFields,
      signatories
    );
  }

  const incidentType = incidentObj?.RecordType?.Name;
  // Map through reportBodyTemplate
  const reportBody = reportBodyTemplate.map((section) => {
    const { sectionLabel, fields } = section;

    // Process each field specification
    const sectionData = (fields as any).reduce(
      (accumulatedData: any, fieldSpec: ReportFields) => {
        const { sourceTab, subSection } = fieldSpec;

        // Check if data source exists
        if (tabSeparatedFormFields[sourceTab]) {
          let selectedFields = getRelevantFields(
            tabSeparatedFormFields,
            fieldSpec,
            incidentTypes,
            incidentType
          );

          // Map the selected fields to the data in the incidentObj
          let formattedSelectedFields = selectedFields
            .map((field: IFieldInstance) => {
              let fieldValue = incidentObj[field.fieldName];
              fieldValue = modifyFieldValue(
                field,
                selectedFields,
                fieldValue,
                allLookUpOptions,
                selectedFields,
                incidentObj
              );
              const shouldRender = shouldReportFieldRender(
                fieldValue,
                field.qapi,
                isNonQapi,
                sectionLabel,
                includeStatements,
                includeConclusion
              );

              // NOTE: the non-QAPI body never includes Incident_Date_Time__c
              // (it lives in the report header, not reportBodyTemplate), so no
              // estimated label-flip is applied here — the header handles it.
              return shouldRender
                ? {
                    fieldName: field.fieldName,
                    fieldLabel: field.fieldLabel,
                    fieldValue: fieldValue,
                  }
                : null;
            })
            .filter(Boolean);

          // If subSection is defined, create a nested object
          divideIntoSubsections(
            subSection,
            accumulatedData,
            formattedSelectedFields
          );
        }

        return accumulatedData;
      },
      {}
    );
    // Return the section data
    return { [sectionLabel]: sectionData };
  });
  appendSignatures(reportBody, signatories);
  return reportBody;
}
